import { useState } from 'react';
import { createPortal } from 'react-dom';
import { IS_MOBILE } from '../config';
import { setHudVar } from './hudStack';

// BottomSlot — the bottom-centre column, as ONE thing.
//
// Khari, 2026-08-04: "HUD UI crashing in the studio" — the session invite, its
// caption, and the live-chat chip all landed on top of each other.
//
// THE DISEASE: every surface that wants to sit at the bottom centre picks its
// own `position: fixed; bottom: <a number somebody eyeballed>`. Each is correct
// alone. Two of them are correct alone AND on top of each other, because
// nothing in the code knows the other exists — the studio had an invite button
// at 96px, its caption at 66px, and a live-chat chip at 110px (mobile), owned by
// two components that have never heard of one another. Adding a third offset
// would have been the fourth guess, not a fix.
//
// THE CURE: one fixed container, laid out as a column, that every bottom-centre
// surface renders INTO. Overlap stops being something to avoid and becomes
// something the layout cannot express. Order is declared (`order`, lowest at the
// top) rather than negotiated in pixels, so a new surface picks a rank, not a
// coordinate.
//
// The container is anchored by its BOTTOM edge with no height, so it grows
// upward as things join it — which is why nothing below has to move when the
// stack gets taller.
//
// Scoped deliberately to the bottom centre. This is the shape the whole HUD
// wants (project_world_hud_layout_pass), and the one place it was actually
// breaking tonight; the rest of the pass can adopt the same container without
// re-deciding anything.
const ID = 'hud-bottom-stack';
let hidden = false;

/** Hide or show the whole stack (a chat scene has the screen, App) — its occupants stay mounted. */
export function hideBottomStack(on) {
  hidden = !!on;
  const el = typeof document !== 'undefined' ? document.getElementById(ID) : null;
  if (el) el.style.display = hidden ? 'none' : 'flex';
}

function ensureNode() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(ID);
  if (el) return el;
  el = document.createElement('div');
  el.id = ID;
  Object.assign(el.style, {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    // Mobile keeps clear of the bottom HUD (now-playing chip, Music button,
    // joystick) — the same allowance the live chip used to make on its own.
    // Elsewhere, on the player bar whenever it runs under the centre (a narrow
    // window: PlayerBar publishes --hud-centre-clear), else at the bottom.
    bottom: IS_MOBILE
      ? 'calc(max(18px, env(safe-area-inset-bottom)) + 110px)'
      : 'max(max(18px, env(safe-area-inset-bottom)), var(--hud-centre-clear, 0px))',
    zIndex: 71,
    display: hidden ? 'none' : 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    // The COLUMN never eats a click; its occupants do. A full-width invisible
    // bar that swallows taps is the other classic HUD bug.
    pointerEvents: 'none',
    maxWidth: 'min(560px, calc(100vw - 24px))',
  });
  document.body.appendChild(el);
  // How high the stack reaches, for what stands over it (the WASD keys,
  // hudStack): --hud-bottom-top, px from the bottom of the screen.
  // Not while hidden (a hidden node measures as nothing); showing it resizes it, which publishes again.
  const publish = () => { if (!hidden) setHudVar('--hud-bottom-top', `${Math.ceil(window.innerHeight - el.getBoundingClientRect().top)}px`); };
  publish();
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(publish).observe(el);
  window.addEventListener('resize', publish);
  window.addEventListener('hud:reflow', publish);   // lifted onto the player bar without resizing
  return el;
}

/**
 * Render children into the bottom-centre stack.
 *
 * @param order  lower sits HIGHER in the column. Leave gaps between ranks so a
 *               new surface can slot between two without renumbering.
 */
export default function BottomSlot({ order = 50, children }) {
  const [node] = useState(() => ensureNode());
  if (!node) return null;
  return createPortal(
    <div style={{ order, pointerEvents: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {children}
    </div>,
    node,
  );
}
